import { useState, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Rocket, Upload } from "lucide-react";

const CHUNK_SIZE = 1.5 * 1024 * 1024;
const LEVELS = ["Beginner", "Low Intermediate", "Intermediate", "High Intermediate", "Advanced", "Contender", "Future Pro", "Pro"];
const lbl = "block text-[9px] uppercase tracking-widest text-zinc-500 mb-1";
const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-white placeholder-zinc-600 focus:outline-none focus:border-pb-cyan text-sm";

export default function StartLeagueForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", facility: "", city: "", state: "", start_date: "", weeks: 6,
    play_day: "", play_time: "", capacity: 16, fee: 0, environment: "Outdoor", description: "",
  });
  const [levels, setLevels] = useState([...LEVELS]);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Image is too big — max 25MB");
    setUploading(true);
    try {
      const uploadId = `league_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      let res = null;
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("total_chunks", total);
        fd.append("filename", file.name);
        fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        res = (await api.post("/community/upload/chunk", fd)).data;
      }
      setImage(res.url);
      toast.success("League image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("League name is required");
    if (!form.start_date) return toast.error("Start date is required");
    if (levels.length === 0) return toast.error("Select at least one skill level");
    setBusy(true);
    try {
      await api.post("/admin/leagues", { ...form, weeks: Number(form.weeks) || 6,
        capacity: Number(form.capacity) || 0, fee: Number(form.fee) || 0, levels, image });
      toast.success(`${form.name} is live — registration is open`);
      onCreated?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="start-league-modal">
      <div className="bg-pb-surface border border-pb-cyan/40 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-pb-border">
          <div>
            <div className="font-display font-black uppercase text-xl flex items-center gap-2"><Rocket className="w-5 h-5 text-pb-cyan" /> Start a League</div>
            <div className="text-xs text-zinc-500 mt-0.5">Coed league — every team plays in one open division ladder split by PSI level.</div>
          </div>
          <button data-testid="start-league-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className={lbl}>League Name *</label>
            <input data-testid="slf-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Bullpen Winter League — Season 2" className={input} /></div>
          <div><label className={lbl}>Facility</label>
            <input data-testid="slf-facility" value={form.facility} onChange={(e) => set("facility", e.target.value)} placeholder="The Pickle Bullpen" className={input} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>City</label>
              <input data-testid="slf-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Sarasota" className={input} /></div>
            <div><label className={lbl}>State</label>
              <input data-testid="slf-state" value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="FL" className={input} /></div>
          </div>
          <div><label className={lbl}>Start Date *</label>
            <input data-testid="slf-start-date" type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={input} /></div>
          <div><label className={lbl}>Season Length (weeks)</label>
            <input data-testid="slf-weeks" type="number" min={1} max={52} value={form.weeks} onChange={(e) => set("weeks", e.target.value)} className={input} /></div>
          <div><label className={lbl}>Play Day</label>
            <input data-testid="slf-play-day" value={form.play_day} onChange={(e) => set("play_day", e.target.value)} placeholder="Thursdays" className={input} /></div>
          <div><label className={lbl}>Play Time</label>
            <input data-testid="slf-play-time" value={form.play_time} onChange={(e) => set("play_time", e.target.value)} placeholder="6:00–9:00 PM" className={input} /></div>
          <div><label className={lbl}>Team Capacity (0 = unlimited)</label>
            <input data-testid="slf-capacity" type="number" min={0} max={500} value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className={input} /></div>
          <div><label className={lbl}>Entry Fee ($ per team, 0 = free)</label>
            <input data-testid="slf-fee" type="number" min={0} step="1" value={form.fee} onChange={(e) => set("fee", e.target.value)} className={input} /></div>
          <div><label className={lbl}>Indoor / Outdoor <span className="text-zinc-600 normal-case tracking-normal">(feeds PSI)</span></label>
            <select data-testid="slf-environment" value={form.environment} onChange={(e) => set("environment", e.target.value)} className={input}>
              <option>Outdoor</option><option>Indoor</option>
            </select></div>
          <div><label className={lbl}>League Image</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <button data-testid="slf-image-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-pb-border text-zinc-400 px-3 py-2 text-xs uppercase tracking-widest hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-40">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {image ? "Replace image" : "Upload image"}
            </button>
            {image && <img src={image} alt="" className="mt-2 h-16 object-contain bg-black border border-pb-border" />}</div>

          <div className="sm:col-span-2">
            <label className={lbl}>Skill Levels * <span className="text-zinc-600 normal-case tracking-normal">(who can join — coed, no event types)</span></label>
            <div className="flex flex-wrap gap-2">
              {LEVELS.map((lv) => (
                <button key={lv} data-testid={`slf-level-${lv.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setLevels((l) => (l.includes(lv) ? l.filter((x) => x !== lv) : [...l, lv]))}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${levels.includes(lv) ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-500 hover:text-white"}`}>
                  {lv}
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2"><label className={lbl}>Description / Rules</label>
            <textarea data-testid="slf-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
              placeholder="Format notes, phase rules, prizes, what players should expect…" className={input} /></div>
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-pb-border">
          <button data-testid="slf-cancel" onClick={onClose} className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest font-bold">Cancel</button>
          <button data-testid="slf-launch" onClick={submit} disabled={busy || uploading}
            className="flex items-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest px-6 py-3 text-sm hover:opacity-90 active:scale-95 transition-all disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Launch League
          </button>
        </div>
      </div>
    </div>
  );
}
